import React from 'react';
import { fmtMoney } from './constants';
import { Users, Siren, FileText, MapPin, Send, Trophy, Ban, Hammer, Sparkles, Languages, DollarSign } from 'lucide-react';

const Stat = ({ label, value, icon: Icon, color, testid }) => (
  <div className={`rounded-lg border p-4 ${color}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-2xl font-bold text-white">{value}</div>
      {Icon && <Icon className="w-5 h-5 opacity-70" />}
    </div>
    <div className="text-xs mt-1 opacity-80">{label}</div>
  </div>
);

const Bar = ({ data, color = 'bg-orange-500' }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const max = Math.max(1, ...entries.map(e => e[1]));
  if (!entries.length) return <p className="text-slate-500 text-xs">No data yet</p>;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3">
          <div className="text-xs text-slate-400 w-32 truncate">{k}</div>
          <div className="flex-1 bg-slate-900 rounded h-4 overflow-hidden">
            <div className={`h-full ${color} rounded`} style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <div className="text-xs text-slate-300 w-6 text-right">{v}</div>
        </div>
      ))}
    </div>
  );
};

export default function StatsPanel({ stats }) {
  if (!stats) return <div className="text-slate-400">Loading stats...</div>;
  return (
    <div className="space-y-6" data-testid="crm-stats">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Stat label="Total Leads" value={stats.total_leads} icon={Users} color="bg-slate-800 border-slate-700 text-slate-300" testid="stat-total-leads" />
        <Stat label="New Leads" value={stats.new_leads} icon={Sparkles} color="bg-blue-950/40 border-blue-500/30 text-blue-300" testid="stat-new-leads" />
        <Stat label="Emergencies" value={stats.emergency_requests} icon={Siren} color="bg-red-950/40 border-red-500/30 text-red-300" testid="stat-emergencies" />
        <Stat label="Estimates Pending" value={stats.estimates_pending} icon={FileText} color="bg-purple-950/40 border-purple-500/30 text-purple-300" />
        <Stat label="Site Visits" value={stats.site_visits} icon={MapPin} color="bg-cyan-950/40 border-cyan-500/30 text-cyan-300" />
        <Stat label="Proposals Sent" value={stats.proposals_sent} icon={Send} color="bg-indigo-950/40 border-indigo-500/30 text-indigo-300" />
        <Stat label="Won Projects" value={stats.won_projects} icon={Trophy} color="bg-green-950/40 border-green-500/30 text-green-300" testid="stat-won" />
        <Stat label="Lost Leads" value={stats.lost_leads} icon={Ban} color="bg-red-950/30 border-red-500/20 text-red-300" />
        <Stat label="Active Projects" value={stats.active_projects} icon={Hammer} color="bg-teal-950/40 border-teal-500/30 text-teal-300" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-green-500/30 bg-green-950/20 p-4 flex items-center gap-4" data-testid="stat-pipeline-value">
          <DollarSign className="w-8 h-8 text-green-400" />
          <div><div className="text-2xl font-bold text-white">{fmtMoney(stats.pipeline_value)}</div><div className="text-xs text-green-300">Estimated Pipeline Value</div></div>
        </div>
        <div className="rounded-lg border border-teal-500/30 bg-teal-950/20 p-4 flex items-center gap-4" data-testid="stat-won-value">
          <Trophy className="w-8 h-8 text-teal-400" />
          <div><div className="text-2xl font-bold text-white">{fmtMoney(stats.won_value)}</div><div className="text-xs text-teal-300">Won Project Value</div></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Leads by Service</h3>
          <Bar data={stats.leads_by_service} color="bg-orange-500" />
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Leads by Source</h3>
          <Bar data={stats.leads_by_source} color="bg-purple-500" />
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2"><Languages className="w-4 h-4" />English vs Spanish</h3>
          <Bar data={{ English: stats.language_breakdown?.en || 0, Spanish: stats.language_breakdown?.es || 0 }} color="bg-teal-500" />
        </div>
      </div>
    </div>
  );
}
